<script lang="ts">
  import type { SlotWithWords } from '@blather/shared';

  export let slot: SlotWithWords;
  export let index: number;
  export let isActive: boolean;
  export let selectedWords: string[] = [];
  export let focusedWordIndex: number = 0;

  $: isSlotFull = selectedWords.length >= slot.maxPerSlot;
  $: slotTitle = slot.defaultWord.charAt(0).toUpperCase() + slot.defaultWord.slice(1);
  $: counter = `${selectedWords.length} / ${slot.maxPerSlot}`;
</script>

<div
  class="word-slot"
  class:active={isActive}
  role="region"
  aria-label="{slotTitle} slot"
  aria-describedby="slot-{index}-desc"
>
  <div class="slot-header">
    <h3 class="slot-title">{slotTitle}</h3>
    <span class="slot-counter" aria-live="polite">{counter}</span>
  </div>

  <div class="slot-selected" id="slot-{index}-desc">
    {#if selectedWords.length > 0}
      <div class="selected-words">
        {#each selectedWords as word}
          <span class="selected-word-chip">{word}</span>
        {/each}
      </div>
    {:else}
      <p class="empty-message">No words selected</p>
    {/if}
  </div>

  <div class="slot-available">
    <div class="word-grid">
      {#each slot.availableWords as word, wordIndex}
        {@const isFocused = isActive && focusedWordIndex === wordIndex}
        {@const isSelected = selectedWords.includes(word)}
        {@const isDisabled = isSlotFull && !isSelected}

        <div
          class="word-item"
          class:focused={isFocused}
          class:selected={isSelected}
          class:disabled={isDisabled}
          role="option"
          aria-selected={isSelected}
          aria-disabled={isDisabled}
        >
          {word}
        </div>
      {/each}
    </div>
  </div>
</div>

<style>
  .word-slot {
    padding: 1rem;
    border: 2px solid #e5e7eb;
    border-radius: 0.5rem;
    background: white;
    transition: all 0.2s;
  }

  .word-slot.active {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  .slot-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .slot-title {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 600;
    color: #1f2937;
  }

  .slot-counter {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
    padding: 0.25rem 0.5rem;
    background: #f3f4f6;
    border-radius: 0.25rem;
  }

  .slot-selected {
    min-height: 3rem;
    margin-bottom: 1rem;
    padding: 0.75rem;
    background: #f9fafb;
    border-radius: 0.375rem;
  }

  .selected-words {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .selected-word-chip {
    display: inline-block;
    padding: 0.375rem 0.75rem;
    background: #3b82f6;
    color: white;
    border-radius: 0.375rem;
    font-weight: 500;
    font-size: 0.875rem;
  }

  .empty-message {
    margin: 0;
    color: #9ca3af;
    font-size: 0.875rem;
    font-style: italic;
  }

  .slot-available {
    margin-top: 1rem;
  }

  .word-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 0.5rem;
  }

  .word-item {
    padding: 0.625rem 0.75rem;
    background: #f3f4f6;
    border: 2px solid transparent;
    border-radius: 0.375rem;
    text-align: center;
    font-weight: 500;
    font-size: 0.9375rem;
    cursor: default;
    transition: all 0.15s;
    user-select: none;
  }

  .word-item.focused {
    background: #3b82f6;
    color: white;
    border-color: #2563eb;
    transform: scale(1.02);
  }

  .word-item.selected {
    background: #d1d5db;
    color: #6b7280;
    text-decoration: line-through;
    opacity: 0.7;
  }

  .word-item.disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  /* Focus indicator for accessibility */
  .word-slot.active .slot-title::before {
    content: '▶ ';
    color: #3b82f6;
    margin-right: 0.25rem;
  }

  /* High contrast mode support */
  @media (prefers-contrast: high) {
    .word-slot.active {
      border-width: 3px;
    }

    .word-item.focused {
      outline: 3px solid #1d4ed8;
      outline-offset: 2px;
    }
  }

  /* Responsive adjustments */
  @media (max-width: 640px) {
    .word-grid {
      grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
      gap: 0.375rem;
    }

    .word-item {
      padding: 0.5rem;
      font-size: 0.875rem;
    }
  }
</style>
